import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../lib/translations';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const { lang, toggleLang } = useLanguage();
  const t = translations[lang].nav;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();

  const isServicesActive = location.pathname.startsWith('/services');

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsMobileServicesOpen(location.pathname.startsWith('/services'));
  }, [location]);

  const handleNavClick = (path) => {
    if (location.pathname === path) {
      window.scrollTo(0, 0);
      setIsMenuOpen(false);
    }
  };

  const navItems = [
    { name: t.about, path: '/om-mig' },
    { 
      name: t.services, 
      isDropdown: true,
      items: [
        { name: t.therapy, path: lang === 'da' ? '/services/terapi' : '/services/therapy' },
        { name: t.coaching, path: lang === 'da' ? '/services/coaching' : '/services/coaching' },
        { name: t.compassion, path: lang === 'da' ? '/services/compassion-fokuseret-terapi' : '/services/compassion-focused-therapy' },
        { name: t.living_abroad, path: lang === 'da' ? '/services/udlandsophold' : '/services/living-abroad' },
        { name: t.supervision, path: lang === 'da' ? '/services/supervision' : '/services/supervision' },
      ]
    },
    { name: t.resources, path: '/ressourcer' },
    { name: t.contact, path: '/kontakt' },
  ];

  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
        when: 'afterChildren',
      },
    },
    open: {
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.4,
        ease: 'easeOut',
        when: 'beforeChildren',
      },
    },
  };

  const itemVariants = {
    closed: { opacity: 0, y: -10 },
    open: { opacity: 1, y: 0 },
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-stone-100/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-24">
          <Link to="/" className="flex items-center group" onClick={() => handleNavClick('/')}>
            <img 
              src={lang === 'da' 
                ? "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/logo-dansk.png" 
                : "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/logo_1.png"
              } 
              alt={lang === 'da' ? "Klinisk psykolog Kay Elizabeth Worsfold Logo" : "Clinical Psychologist Kay Elizabeth Worsfold Logo"} 
              className="h-12 md:h-14 w-auto object-contain transition-opacity group-hover:opacity-80"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden min-[1240px]:flex items-center space-x-6 min-[1340px]:space-x-8">
            {navItems.map((item) => {
              if (item.isDropdown) {
                return (
                  <div 
                    key="services-dropdown"
                    className="relative py-2"
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <button 
                      className={`text-[12px] uppercase tracking-widest transition-all duration-300 flex items-center gap-1.5 py-2 ${
                        isServicesActive ? 'text-stone-900 font-medium' : 'text-stone-400 hover:text-taupe-500'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      {isServicesActive && (
                        <motion.div
                          layoutId="nav-underline"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-taupe-400"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </button>

                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 bg-white rounded-xl shadow-lg border border-stone-100 py-3 z-50"
                        >
                          {item.items.map((subItem) => (
                            <NavLink
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => {
                                handleNavClick(subItem.path);
                                setIsDropdownOpen(false);
                              }}
                              className={({ isActive }) =>
                                `block px-6 py-3 text-[11px] uppercase tracking-widest transition-colors ${
                                  isActive ? 'text-stone-900 bg-stone-50 font-medium' : 'text-stone-400 hover:text-taupe-500 hover:bg-stone-50'
                                }`
                              }
                            >
                              {subItem.name}
                            </NavLink>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              if (item.isButton) {
                return (
                  <Link
                    key="book-btn"
                    to={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className="px-5 py-2 border border-taupe-400 text-taupe-400 rounded-full hover:bg-taupe-400 hover:border-taupe-400 hover:text-white transition-all duration-300 font-medium text-[12px] uppercase tracking-widest"
                  >
                    {item.name}
                  </Link>
                );
              }

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={({ isActive }) =>
                    `text-[12px] uppercase tracking-widest transition-all duration-300 hover:text-taupe-500 relative py-2 ${
                      isActive ? 'text-stone-900 font-medium' : 'text-stone-400'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.name}
                      {isActive && (
                        <motion.div
                          layoutId="nav-underline"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-taupe-400"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
            
            <button
              onClick={toggleLang}
              className="flex items-center space-x-2 text-[13px] uppercase tracking-widest text-stone-400 hover:text-stone-900 transition-colors ml-4"
            >
              <span className="text-base leading-none">{lang === 'da' ? '🇬🇧' : '🇩🇰'}</span>
              <span>{lang === 'da' ? 'EN' : 'DA'}</span>
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="min-[1240px]:hidden flex items-center space-x-3">
            <button
              onClick={toggleLang}
              className="flex items-center space-x-2 px-3 py-2 bg-stone-50/80 border border-stone-200/60 rounded-full text-stone-600 hover:text-stone-900 hover:bg-stone-100 active:scale-95 transition-all text-sm font-medium min-h-[40px]"
              aria-label={lang === 'da' ? 'Skift til engelsk' : 'Switch to Danish'}
            >
              <span className="text-base leading-none">{lang === 'da' ? '🇬🇧' : '🇩🇰'}</span>
              <span className="text-xs uppercase tracking-widest font-semibold">{lang === 'da' ? 'EN' : 'DA'}</span>
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative w-10 h-10 flex items-center justify-center text-stone-900 hover:bg-stone-50 rounded-full transition-colors z-50"
              aria-label="Toggle menu"
            >
              <div className="flex flex-col gap-1.5 w-6 items-center justify-center">
                <motion.span
                  animate={isMenuOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
                  className="w-full h-0.5 bg-current block origin-center"
                />
                <motion.span
                  animate={isMenuOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                  className="w-full h-0.5 bg-current block"
                />
                <motion.span
                  animate={isMenuOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
                  className="w-full h-0.5 bg-current block origin-center"
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="min-[1240px]:hidden bg-white overflow-hidden border-t border-stone-100"
          >
            <div className="px-6 pt-4 pb-10 space-y-2">
              {navItems.map((item, index) => {
                if (item.isDropdown) {
                  return (
                    <motion.div
                      key="services-mobile-dropdown"
                      variants={itemVariants}
                      transition={{ delay: index * 0.05 }}
                      className="space-y-1"
                    >
                      <button
                        onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                        className={`w-full flex items-center justify-between px-4 py-4 text-lg font-serif transition-colors rounded-lg ${
                          isServicesActive ? 'text-stone-900 bg-stone-50' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      <AnimatePresence initial={false}>
                        {isMobileServicesOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden pl-6 space-y-1"
                          >
                            {item.items.map((subItem) => (
                              <NavLink
                                key={subItem.path}
                                to={subItem.path}
                                onClick={() => {
                                  handleNavClick(subItem.path);
                                  setIsMenuOpen(false);
                                }}
                                className={({ isActive }) =>
                                  `block px-4 py-3 text-base font-serif transition-colors rounded-lg ${
                                    isActive ? 'text-stone-900 bg-stone-50/50 font-medium' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'
                                  }`
                                }
                              >
                                {subItem.name}
                              </NavLink>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                }

                if (item.isButton) {
                  return (
                    <motion.div
                      key="book-btn"
                      variants={itemVariants}
                      transition={{ delay: index * 0.05 }}
                      className="pt-4 px-4"
                    >
                      <Link
                        to={item.path}
                        onClick={() => handleNavClick(item.path)}
                        className="block text-center px-4 py-3 border border-taupe-400 text-taupe-400 rounded-full hover:bg-taupe-400 hover:border-taupe-400 hover:text-white transition-all duration-300 font-medium text-base uppercase tracking-wider"
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  );
                }

                return (
                  <motion.div
                    key={item.path}
                    variants={itemVariants}
                    transition={{ delay: index * 0.05 }}
                  >
                    <NavLink
                      to={item.path}
                      onClick={() => handleNavClick(item.path)}
                      className={({ isActive }) =>
                        `block px-4 py-4 text-lg font-serif transition-colors rounded-lg ${
                          isActive ? 'text-stone-900 bg-stone-50' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'
                        }`
                      }
                    >
                      {item.name}
                    </NavLink>
                  </motion.div>
                );
              })}

              {/* Mobile Language Switcher inside menu */}
              <motion.div
                variants={itemVariants}
                transition={{ delay: navItems.length * 0.05 }}
                className="pt-6 px-4 border-t border-stone-100 mt-4"
              >
                <div className="text-[11px] uppercase tracking-widest text-stone-400 mb-3 font-medium">
                  {lang === 'da' ? 'Vælg sprog / Select language' : 'Select language / Vælg sprog'}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      if (lang !== 'da') toggleLang();
                    }}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl border text-sm font-medium transition-all active:scale-98 ${
                      lang === 'da'
                        ? 'bg-taupe-50 border-taupe-300 text-taupe-800 font-semibold'
                        : 'bg-white border-stone-200 text-stone-500 hover:bg-stone-50'
                    }`}
                  >
                    <span className="text-base">🇩🇰</span>
                    <span>Dansk</span>
                  </button>
                  <button
                    onClick={() => {
                      if (lang !== 'en') toggleLang();
                    }}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl border text-sm font-medium transition-all active:scale-98 ${
                      lang === 'en'
                        ? 'bg-taupe-50 border-taupe-300 text-taupe-800 font-semibold'
                        : 'bg-white border-stone-200 text-stone-500 hover:bg-stone-50'
                    }`}
                  >
                    <span className="text-base">🇬🇧</span>
                    <span>English</span>
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
